import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { CatalogOffering, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryReplenishmentPolicy } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-replenishment-policies',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, CheckboxModule, InputTextModule, SelectModule, TableModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.replenishment.title')" [description]="t('catalog.replenishment.description')">
      <ain-inventory-page-hint pageKey="replenishmentPolicies" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="form-card">
          <h3>{{ t('catalog.replenishment.createTitle') }}</h3>
          <div class="form-grid">
            <label><span>{{ t('catalog.inventory.offeringId') }}</span><p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" /></label>
            <label><span>{{ t('catalog.inventory.warehouseId') }}</span><p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" (ngModelChange)="onWarehouseChange()" /></label>
            <label><span>{{ t('catalog.replenishment.binOptional') }}</span><p-select [(ngModel)]="form.binId" [options]="binOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" showClear="true" /></label>
            <label><span>{{ t('catalog.replenishment.minimum') }}</span><input pInputText type="number" [(ngModel)]="form.minimumQuantity" /></label>
            <label><span>{{ t('catalog.replenishment.reorderPoint') }}</span><input pInputText type="number" [(ngModel)]="form.reorderPoint" /></label>
            <label><span>{{ t('catalog.replenishment.maximum') }}</span><input pInputText type="number" [(ngModel)]="form.maximumQuantity" /></label>
            <label><span>{{ t('catalog.replenishment.safetyStock') }}</span><input pInputText type="number" [(ngModel)]="form.safetyStock" /></label>
            <label><span>{{ t('catalog.replenishment.leadTimeDays') }}</span><input pInputText type="number" [(ngModel)]="form.leadTimeDays" /></label>
            <label class="check"><p-checkbox [binary]="true" [(ngModel)]="form.isActive" /><span>{{ t('catalog.replenishment.active') }}</span></label>
          </div>
          <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.replenishment.manage'" [label]="t('catalog.action.create')" (click)="create()"></button>
        </section>

        <section class="list-card">
          <div class="toolbar">
            <a routerLink="/inventory/replenishment-breaches">{{ t('catalog.replenishment.viewBreaches') }}</a>
          </div>
          <p-table [value]="policies()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.replenishment.reorderPoint') }}</th>
                <th>{{ t('catalog.replenishment.minimum') }}</th>
                <th>{{ t('catalog.replenishment.maximum') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ offeringLabel(row.offeringId) }}</td>
                <td>{{ warehouseLabel(row.warehouseId) }}</td>
                <td>{{ row.reorderPoint }}</td>
                <td>{{ row.minimumQuantity }}</td>
                <td>{{ row.maximumQuantity }}</td>
                <td><a [routerLink]="['/inventory/replenishment-policies', row.id]">{{ t('common.open') }}</a></td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .form-card, .list-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.check { display: flex; align-items: center; gap: 8px; }
    .toolbar { margin-bottom: 8px; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryReplenishmentPoliciesComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly policies = signal<InventoryReplenishmentPolicy[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);

  form = {
    offeringId: '',
    warehouseId: '',
    binId: null as string | null,
    minimumQuantity: 5,
    reorderPoint: 10,
    maximumQuantity: 50,
    safetyStock: 2,
    leadTimeDays: 7,
    isActive: true
  };

  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  offeringOptions = () => this.offerings().filter(o => o.inventoryEnabled).map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  binOptions = () => this.bins().map(b => ({ label: `${b.code} — ${b.name}`, value: b.id }));
  offeringLabel = (id: string) => this.offerings().find(o => o.id === id)?.code ?? id;
  warehouseLabel = (id: string) => this.warehouses().find(w => w.id === id)?.code ?? id;

  t(key: string): string { return this.localization.translate(key); }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load(false)
    );
    void this.load();
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
  }

  onWarehouseChange(): void {
    this.form.binId = null;
    if (!this.form.warehouseId) { this.bins.set([]); return; }
    this.service.listWarehouseBins(this.form.warehouseId).subscribe({ next: b => this.bins.set(b), error: () => this.bins.set([]) });
  }

  create(): void {
    this.error.set('');
    if (!this.form.offeringId || !this.form.warehouseId) {
      this.error.set(this.t('catalog.replenishment.requiredFields'));
      return;
    }
    this.service.createInventoryReplenishmentPolicy({ ...this.form }).subscribe({
      next: p => void this.router.navigate(['/inventory/replenishment-policies', p.id]),
      error: () => this.error.set(this.t('catalog.replenishment.createFailed'))
    });
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listInventoryReplenishmentPolicies().subscribe({
      next: list => { this.policies.set(list); this.loading.set(false); },
      error: () => { this.error.set(this.t('catalog.replenishment.loadFailed')); this.loading.set(false); }
    });
  }
}
